import { type Entity, mapEntity } from '@api';
import { formatPriceDisplay } from '@terminal/components/price-display-view/helpers/price-display-helpers';
import {
  mapEntityToPriceEntity,
  useLivePrice,
} from '@terminal/hooks/use-live-price';
import { usePreliminaryPrice } from '@terminal/react-query-hooks/prices/usePreliminaryPrice';
import { useSnakeMeta } from '@terminal/react-query-hooks/snakes/useSnake';
import { getEntityPriceVenue } from '@terminal/services/price/price-service';
import { getPriceData } from '@terminal/widgets/asset-overview-widget/utils/asset-box/asset-box-helpers';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import styles from './company-card/CompanyCard.module.scss';
import { isReferenceDateOlderThanTwoDays } from './utils';

export interface PriceChangeProps {
  className?: string;
  entity: Entity;
  showPostfix?: boolean;
  referenceDate?: string;
  shouldDisableLive?: boolean;
  liveDataText?: string;
  lastCloseText?: string;
}

export const PriceChange = ({
  className,
  entity,
  referenceDate,
  liveDataText,
  lastCloseText,
  showPostfix = true,
  shouldDisableLive = isReferenceDateOlderThanTwoDays(referenceDate),
}: PriceChangeProps) => {
  const { t } = useTranslation(['earnings']);

  const mappedEntity = mapEntity(entity);
  const { data: snakeMeta } = useSnakeMeta(entity?.default_snake);
  const { data: preliminaryPrice } = usePreliminaryPrice(
    entity.subscribable_ticker ?? '',
    getEntityPriceVenue(entity)
  );

  const priceEntities = mapEntityToPriceEntity(entity, snakeMeta);

  const livePriceData = useLivePrice(shouldDisableLive ? [] : priceEntities);
  const livePriceInfo = getPriceData({
    hoverData: undefined,
    livePriceData,
    entity: mappedEntity,
    ts: [],
    priceEntity: priceEntities[0],
  });
  const price = formatPriceDisplay({
    exchange: entity.exchange.code,
    livePriceInfo,
    preliminaryPrice: preliminaryPrice?.data,
    meta: snakeMeta,
    isHovered: false,
    t,
  });

  if (!livePriceInfo || !price.primary.priceChange) {
    return null;
  }

  const hasLivePrice = !!price.primary?.isLive || !!price.secondary?.isLive;
  const priceChange = price.secondary?.priceChange
    ? price.secondary.priceChange
    : price.primary?.priceChange;
  const outsideHoursTag =
    price.secondary?.isLive && price.secondary?.outsideHours?.tag
      ? price.secondary.outsideHours.tag
      : 'open';

  const liveDataLabel =
    liveDataText || t('earningsInsight.drawer.earningsRecap.liveData');
  const lastCloseLabel =
    lastCloseText || t('earningsInsight.drawer.earningsRecap.asOfLastClose');

  return (
    <span className={clsx(styles.priceChangeWrapper, className)}>
      <span
        className={clsx(styles.priceChangeContainer, {
          [styles.priceChangeContainerPositive]:
            priceChange.status === 'positive',
          [styles.priceChangeContainerNegative]:
            priceChange.status === 'negative',
          [styles.priceChangeContainerDefault]:
            priceChange.status === 'default',
        })}
      >
        {priceChange.proportionChange}
      </span>
      {showPostfix && (
        <span
          className={clsx(styles.dateLabel, {
            [styles[outsideHoursTag]]: hasLivePrice,
          })}
        >
          {hasLivePrice ? liveDataLabel : lastCloseLabel}
        </span>
      )}
    </span>
  );
};
